import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["ari", "containerAri", "store"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FabricElementsAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Reactions, UfoErrorBoundary } from '../../components';
import { ufoExperiences } from '../../store';
import { ReactionStatus } from '../../types';

/**
 * State Props required for <Reactions />
 */

/**
 * Dispatch Props required for <Reactions />
 */

/**
 * Export the mapper function outside the component so easier to do unit tests
 */
export var mapStateToPropsHelper = function mapStateToPropsHelper(containerAri, ari, state) {
  var reactionsState = state && state.reactions["".concat(containerAri, "|").concat(ari)];
  if (!state || !reactionsState) {
    return {
      status: ReactionStatus.notLoaded,
      reactions: []
    };
  }
  switch (reactionsState.status) {
    case ReactionStatus.ready:
      return {
        reactions: reactionsState.reactions,
        status: reactionsState.status,
        flash: state.flash["".concat(containerAri, "|").concat(ari)]
      };
    case ReactionStatus.error:
      return {
        status: ReactionStatus.error,
        reactions: []
      };
    default:
      return {
        status: ReactionStatus.loading,
        reactions: []
      };
  }
};

/**
 * Export the mapper function outside the component so easier to do unit tests
 */
export var mapDispatchToPropsHelper = function mapDispatchToPropsHelper(actions, containerAri, ari) {
  return {
    loadReaction: function loadReaction() {
      actions.getReactions(containerAri, ari);
    },
    onReactionClick: function onReactionClick(emojiId) {
      actions.toggleReaction(containerAri, ari, emojiId);
    },
    getReactionDetails: function getReactionDetails(emojiId) {
      actions.getDetailedReaction(containerAri, ari, emojiId);
    },
    onSelection: function onSelection(emojiId) {
      actions.addReaction(containerAri, ari, emojiId);
    }
  };
};
export var ConnectedReactionsView = function ConnectedReactionsView(props) {
  var ari = props.ari,
    containerAri = props.containerAri,
    store = props.store,
    rest = _objectWithoutProperties(props, _excluded);
  /**
   * Reference to the <Reactions /> component instance mandatory props
   */
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    stateData = _useState2[0],
    setStateData = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    dispatchData = _useState4[0],
    setDispatchData = _useState4[1];
  // compose a UFO experience object
  var experienceInstance = useRef();
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  useEffect(function () {
    experienceInstance.current = ufoExperiences.render(ari);
  }, [ari]);

  // abort when component gets unmounted
  useEffect(function () {
    return function cleanup() {
      var _experienceInstance$c;
      (_experienceInstance$c = experienceInstance.current) === null || _experienceInstance$c === void 0 ? void 0 : _experienceInstance$c.abort({
        metadata: {
          source: 'ConnectedReactionsView',
          ari: ari,
          containerAri: containerAri,
          reason: 'unmount'
        }
      });
    };
  }, [experienceInstance, containerAri, ari]);

  /**
   * Maps the store instance "reactions" data to the <Reactions /> component state data
   * @param state store instance latest data
   */
  var mapStateToProps = useCallback(function (state) {
    return mapStateToPropsHelper(containerAri, ari, state);
  }, [containerAri, ari]);

  /**
   * Map the store instance "actions" methods to the <Reactions /> component actions methods
   * @param actions list of actions in the store instance
   */
  var mapDispatchToProps = useCallback(function (actions) {
    return mapDispatchToPropsHelper(actions, containerAri, ari);
  }, [ari, containerAri]);
  useEffect(function () {
    _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var _store, state, dispatch;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return Promise.resolve(store);
          case 2:
            _store = _context.sent;
            if (_store.setCreateAnalyticsEvent && createAnalyticsEvent) {
              _store.setCreateAnalyticsEvent(createAnalyticsEvent);
            }
            state = mapStateToProps(_store.getState());
            dispatch = mapDispatchToProps(_store);
            setStateData(state);
            setDispatchData(dispatch);
          case 8:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }))();
  }, [createAnalyticsEvent, store, ari, containerAri, mapStateToProps, mapDispatchToProps]);

  /**
   * Listen to changes on the store data.
   * TODO: Needs to be refactored inside the MemoryReactionsStore to different pattern, currently it relies on internal callbacks variable to trigger changes to the store.
   * The flow that each action method triggers a call to "setState" => "triggerOnChange" => calls the callback function registered by the onChange method and de-register by the "removeOnChangeListener" method
   */
  useEffect(function () {
    var onChangeCallback = function onChangeCallback(state) {
      var stateProps = mapStateToProps(state);
      setStateData(stateProps);
    };
    _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      var _store;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return Promise.resolve(store);
          case 2:
            _store = _context2.sent;
            _store.onChange(onChangeCallback);
          case 4:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }))();
    return function cleanup() {
      _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        var _store;
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) switch (_context3.prev = _context3.next) {
            case 0:
              _context3.next = 2;
              return Promise.resolve(store);
            case 2:
              _store = _context3.sent;
              _store.removeOnChangeListener(onChangeCallback);
            case 4:
            case "end":
              return _context3.stop();
          }
        }, _callee3);
      }))();
    };
  }, [mapStateToProps, store]);
  return /*#__PURE__*/React.createElement(FabricElementsAnalyticsContext, {
    data: {
      containerAri: containerAri,
      ari: ari
    }
  }, /*#__PURE__*/React.createElement(UfoErrorBoundary, {
    experiences: experienceInstance.current ? [experienceInstance.current] : []
  }, stateData && dispatchData ? /*#__PURE__*/React.createElement(Reactions, _extends({
    key: "".concat(containerAri, "|").concat(ari)
  }, rest, dispatchData, stateData)) : null));
};